import { useEffect, useMemo } from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';

import { emitter } from '@app/Services/EventEmitter';

import { Forbidden } from '@app/Pages/Forbidden/Forbidden';
import { NavSidebar } from '@app/Components/NavSidebar/NavSidebar';
import Header from '@app/Components/Header/Header';
import ErrorBoundary from "@app/Components/ErrorBoundary";
import { Modals } from '@app/Components/Modal/Modal';
import { NotificationContainer } from '@app/Components/ToastNotification';
import { ToolTipContainer } from '@app/Components/TooltipItem/ToolTipItem';
import Banners from "@app/Components/Banners";
import './_layout.scss';

export const Layout: React.FC<React.PropsWithChildren> = observer(({ children }) => {
    const store = useMemo(() => new LayoutStore(), []);
    const { hasErrors, url, method, error } = store;
    const { pathname } = useLocation();

    useEffect(() => {
        store.clearError();
    }, [pathname]);

    useEffect(() => {
        return () => store.unload();
    }, []);

    return (
        <ErrorBoundary>
            <div className="base-layout">
                <Banners />
                <Header />
                <div className="base-component">
                    <NavSidebar />
                    <div className="base-content">
                        <ErrorBoundary>
                            {hasErrors
                                ? <Forbidden url={url} method={method} error={error} />
                                : children
                            }
                        </ErrorBoundary>
                    </div>
                </div>
            </div>

            <Modals />
            <NotificationContainer />
            <ToolTipContainer />
        </ErrorBoundary>
    );
});

class LayoutStore {
    @observable hasErrors: boolean = false;
    @observable url: null | string = null;
    @observable method: null | string = null;
    @observable error: null | string = null;

    constructor() {
        makeObservable(this);
        emitter.on('notify-error-403', this.onErrorHandler);
    }

    unload() {
        emitter.off('notify-error-403', this.onErrorHandler);
    }

    @action.bound
    onErrorHandler(details: { config?: { url: string, method: string }, response?: { data?: { error: string } } }) {
        this.hasErrors = true;
        this.url = details.config?.url || null;
        this.method = details.config?.method || null;
        this.error = details.response?.data?.error || null;
    }

    @action.bound
    clearError() {
        this.hasErrors = false;
        this.url = null;
        this.method = null;
        this.error = null;
    }
}
